import { useMemo } from "react";
import useOrganizationSharings from "../../../../../../../../../hooks/useOrganizationSharings";
import useOrganizationMembers from "../../../../../../../../../hooks/useOrganizationMembers";
import useEventDetails from "../../../../../../../../events/pages/event/hooks/useEventDetails";
import type { ParticipantOption } from "../components/ParticipantsSelectInput";
import useProjectDetails from "../../../../../hooks/useProjectDetails";

interface UseActivityDataParams {
  eventId?: string;
  projectId?: string;
}

export function useActivityData({ eventId, projectId }: UseActivityDataParams) {
  const { loading: organizationSharingsLoading, externalUserOrganizations } =
    useOrganizationSharings();
  const { data: organizationMembersData, loading: organizationMembersLoading } =
    useOrganizationMembers();
  const { data: eventDetailsData, loading: eventDetailsLoading } =
    useEventDetails(eventId);
  const { data: projectDetailsData, loading: projectDetailsLoading } =
    useProjectDetails(projectId);

  const eventParticipants = useMemo(() => {
    if (projectId) {
      return [];
    }

    return (
      eventDetailsData?.event?.eventParticipants
        ?.filter(
          (participant): participant is NonNullable<typeof participant> =>
            participant !== null && participant !== undefined
        )
        .map((participant) => participant.publicUserOrganization) || []
    );
  }, [eventDetailsData?.event?.eventParticipants, projectId]);

  const projectParticipants = useMemo(() => {
    if (eventId) {
      return [];
    }

    return (
      projectDetailsData?.project?.projectParticipants
        ?.filter(
          (participant): participant is NonNullable<typeof participant> =>
            participant !== null && participant !== undefined
        )
        .map((participant) => participant.publicUserOrganization) || []
    );
  }, [projectDetailsData?.project?.projectParticipants, eventId]);

  const joinedUserOrganizations = useMemo(() => {
    return [
      ...(!eventId && !projectId ? externalUserOrganizations || [] : []),
      ...projectParticipants,
      ...eventParticipants,
      ...(organizationMembersData?.organizationMembers || []),
    ]
      .filter((org) => org !== null && org !== undefined)
      .filter(
        (org, index, self) => index === self.findIndex((t) => t?.id === org?.id)
      );
  }, [
    eventId,
    projectId,
    externalUserOrganizations,
    projectParticipants,
    eventParticipants,
    organizationMembersData?.organizationMembers,
  ]);

  // Transform the data for react-select
  const participantOptions: ParticipantOption[] = joinedUserOrganizations.map(
    (userOrganization) => ({
      value: userOrganization.id,
      userOrganizationId: userOrganization.id,
      label: userOrganization.name,
      organizationId: userOrganization.organizationId,
    })
  );

  const isLoading =
    organizationSharingsLoading ||
    eventDetailsLoading ||
    organizationMembersLoading ||
    projectDetailsLoading;

  // Derive effective projectId: direct projectId OR from the event's project
  const effectiveProjectId = useMemo(() => {
    if (projectId) return projectId;
    if (eventDetailsData?.event?.projectId) return eventDetailsData.event.projectId;
    return undefined;
  }, [projectId, eventDetailsData?.event?.projectId]);

  return {
    isLoading,
    participantOptions,
    eventDetailsData,
    effectiveProjectId,
  };
}
